import { useEffect, useState } from "react";
import { api, type RegisteredIssuer } from "../api/client";
import type { components } from "../api/schema";
import { figure, isMasked, type FigureKind } from "./format";
import { WeeklyLine } from "./WeeklyLine";

type Insights = components["schemas"]["Insights"];
type InsightCell = components["schemas"]["InsightCell"];
export type Highlight = components["schemas"]["InsightHighlight"];

const RUNG_LABEL: Record<string, string> = { self_claimed: "Self-claimed", peer_vouched: "Peer-vouched", affiliated: "Affiliated",
  organisation_confirmed: "Confirmed by an organisation", anchored: "Anchored" };

/** A cell's count, phrased: the panel's only way to show a figure (format.ts). */
const fig = (cell: InsightCell, kind: FigureKind) => (isMasked(cell) ? figure(cell, kind) : figure(cell.count, kind));
/** The people behind a cell, phrased ("across N people"), or nothing for a masked cell (its count already says so). */
const ppl = (cell: InsightCell) => (isMasked(cell) ? "" : figure(cell.people, "people"));
const title = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * /live insights: five views over published claims and vouches (GET /live/insights), narrowed by
 * one organisation filter. Masking is the rail's; phrasing is format.ts's. A tag or anchor row
 * highlights its part of the graph (tap again to clear); a masked row cannot be tapped.
 */
export function InsightsPanel({ onHighlight, replaying, focused = false }: { onHighlight: (h: Highlight | null) => void; replaying: boolean; focused?: boolean }) {
  const [scope, setScope] = useState("");
  const [data, setData] = useState<Insights | null>(null);
  const [err, setErr] = useState("");
  const [orgs, setOrgs] = useState<RegisteredIssuer[]>([]);
  const [lit, setLit] = useState("");

  useEffect(() => { api.GET("/registry/issuers").then((r) => setOrgs((r.data?.issuers ?? []).filter((i) => i.listed !== false))); }, []);
  useEffect(() => {
    setErr(""); setLit(""); onHighlight(null);
    api.GET("/live/insights", { params: { query: scope ? { scope } : {} } })
      .then((r) => (r.data ? setData(r.data) : setErr("No insights for that organisation.")));
  }, [scope]);   // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string, h?: Highlight) => {
    if (!h) return;
    if (lit === key) { setLit(""); onHighlight(null); } else { setLit(key); onHighlight(h); }
  };
  const orgName = (did: string) => { const o = orgs.find((x) => x.did === did); return o ? (o.kind === "light" ? o.domain ?? o.name : o.name) : "An organisation"; };
  const row = (key: string, h: Highlight | undefined, children: React.ReactNode, attrs: Record<string, string>) => h
    ? <li {...attrs} role="button" tabIndex={0} aria-pressed={lit === key} className={`ins-row tappable${lit === key ? " on" : ""}`}
          onClick={() => toggle(key, h)} onKeyDown={(e) => { if (e.key === "Enter") toggle(key, h); }}>{children}</li>
    : <li {...attrs} className="ins-row">{children}</li>;

  return (
    <aside className="live-insights card" data-testid="insights" aria-label="Insights">
      <label htmlFor="ins-scope" className="ins-label">Community or organisation</label>
      <select id="ins-scope" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="insights-scope">
        <option value="">The whole rail</option>
        {orgs.map((o) => <option key={o.did} value={o.did}>{o.kind === "light" ? o.domain ?? o.name : o.name}</option>)}
      </select>
      {focused && <p className="note" data-testid="insights-focus-note">These insights cover the whole graph (or the organisation picked above), not only the fan on screen.</p>}
      {err && <p className="error" role="alert">{err}</p>}
      {data && (<>
        <section data-testid="insights-rungs">
          <h3>How strong the chains are</h3>
          <div className="ins-headline" data-testid="insights-strong-chain">{fig(data.rungs.strong_chain, "claims")} on a strong chain</div>
          {!isMasked(data.rungs.strong_chain_share) && (
            <div className="ins-bar" aria-hidden="true"><span style={{ width: `${Math.round(Number(data.rungs.strong_chain_share) * 100)}%` }} /></div>)}
          <ul className="ins-list">{data.rungs.by_rung.map((r) => (
            <li key={r.rung} className="ins-row" data-rung={r.rung}><span>{RUNG_LABEL[r.rung]}</span><span>{fig(r.claims, "claims")}</span></li>))}</ul>
        </section>
        <section data-testid="insights-tags">
          <h3>Top tags</h3>
          <ul className="ins-list">{data.tags.slice(0, 8).map((t) => row(`tag:${t.tag}`, t.highlight, (<>
            <span className="ins-key">{t.tag}</span>
            <span className="ins-figs">{fig(t.claims, "claims")} · {fig(t.vouched, "vouched")} · {fig(t.anchored, "anchored")}
              {ppl(t.claims) && <span className="ins-ppl"> · {ppl(t.claims)}</span>}</span></>), { key: t.tag, "data-tag": t.tag }))}</ul>
        </section>
        <section data-testid="insights-anchors">
          <h3>Top anchors</h3>
          {data.anchors.length === 0 ? <p className="hint">None yet.</p> : (
            <ul className="ins-list">{data.anchors.slice(0, 6).map((a) => row(`anchor:${a.issuer_did}`, a.highlight, (<>
              <span className="ins-key" title={a.issuer_did}>{orgName(a.issuer_did)}</span><span>{fig(a.claims, "anchored")}</span></>),
              { key: a.issuer_did, "data-issuer": a.issuer_did }))}</ul>)}
        </section>
        <section data-testid="insights-regions">
          <h3>By region</h3>
          {data.regions.length === 0 ? <p className="hint">No region published yet.</p> : (
            <ul className="ins-list">{data.regions.slice(0, 40).map((r) => (
              <li key={`${r.region}/${r.tag}`} className="ins-row" data-region={r.region} data-tag={r.tag}>
                <span className="ins-key">{title(r.region)} · {r.tag}</span><span>{fig(r.claims, "claims")}</span></li>))}</ul>)}
        </section>
        <section data-testid="insights-weeks">
          <h3>Week by week</h3>
          <WeeklyLine weeks={data.weeks} replaying={replaying} />
        </section>
      </>)}
    </aside>
  );
}
